import React from 'react';
import { Box, Typography } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { documentHref } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/** A titled block on a detail page. Renders nothing when it has no content. */
export const Section: React.FC<{
    title: string;
    children: React.ReactNode;
    /** Render even when children look empty — for sections that say "none". */
    always?: boolean;
}> = ({ title, children, always }) => {
    const empty = children === null || children === undefined
        || (Array.isArray(children) && children.every((c) => c === null || c === false));
    if (empty && !always) return null;

    return (
        <Box sx={{ mt: 3.5 }}>
            <Typography
                component="h2"
                sx={{
                    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em',
                    textTransform: 'uppercase', color: dsVar('fg-muted'), mb: 1.25,
                }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );
};

/** A label/value pair. Returns null when there is no value, so callers need no guard. */
export const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <Box sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mb: 0.25 }}>{label}</Typography>
            <Typography component="div" sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.65 }}>
                {value}
            </Typography>
        </Box>
    );
};

export const Prose: React.FC<{ children?: string | null }> = ({ children }) => {
    if (!children) return null;
    return (
        <Typography
            component="p"
            sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.7, m: 0, mb: 1.5 }}
        >
            {children}
        </Typography>
    );
};

/** A string list. `ordered` for steps, where the sequence carries meaning. */
export const BulletList: React.FC<{ items?: unknown; ordered?: boolean }> = ({ items, ordered }) => {
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    if (list.length === 0) return null;

    return (
        <Box
            component={ordered ? 'ol' : 'ul'}
            sx={{ m: 0, mb: 1.5, pl: 2.5, listStyleType: ordered ? 'decimal' : 'disc' }}
        >
            {list.map((item, index) => (
                <Typography
                    component="li"
                    key={index}
                    sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.7, mb: 0.4 }}
                >
                    {String(item)}
                </Typography>
            ))}
        </Box>
    );
};

/** Monospace code, used for every raw identifier. */
export const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        component="span"
        sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', color: dsVar('fg-2') }}
    >
        {children}
    </Typography>
);

/** A list of codes as links, falling back to plain text for an unlinkable code. */
export const CodeLinks: React.FC<{
    codes?: unknown;
    href: (code: string) => string | null;
}> = ({ codes, href }) => {
    const list = Array.isArray(codes) ? codes.filter(Boolean).map(String) : [];
    if (list.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {list.map((code) => {
                const target = href(code);
                const sx = {
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.75rem',
                    px: 1, py: 0.4,
                    borderRadius: '4px',
                    border: `1px solid ${dsVar('line')}`,
                    backgroundColor: dsVar('page'),
                };
                return target ? (
                    <Box
                        key={code}
                        component="a"
                        href={target}
                        sx={{
                            ...sx,
                            color: dsVar('accent-fg'),
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline', borderColor: dsVar('accent-fg') },
                        }}
                    >
                        {code}
                    </Box>
                ) : (
                    <Box key={code} component="span" sx={{ ...sx, color: dsVar('fg-2') }}>
                        {code}
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Citations, rendered in the shape §8.5 defines.
 *
 * `source_link` is required to be http(s) by validation precisely so it can be rendered
 * as a followable link here. A citation with no link is still shown — the pinpoint and
 * identifier are the citation; the link is a convenience.
 *
 * A citation carrying `source_doc_code` ALSO links inward, to that document's registry
 * entry and its blast radius. That is the return leg of the round trip: a reader who
 * arrives at a term and sees which instrument defines it can now ask what else that
 * instrument reaches, without knowing the registry exists. A citation without the code —
 * one that names only an identifier or a URL — renders exactly as it always did.
 */
export const SourceRefs: React.FC<{ refs?: unknown }> = ({ refs }) => {
    const list = Array.isArray(refs) ? refs.filter((r) => r && typeof r === 'object') : [];
    if (list.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {list.map((ref: any, index) => {
                const head = ref.title || ref.document_identifier || ref.source_link || 'Untitled source';
                const meta = [ref.issuer, ref.issuance_date].filter(Boolean).join(', ');
                const registryHref = documentHref(ref.source_doc_code);
                return (
                    <Box key={index} sx={{ pl: 1.5, borderLeft: `2px solid ${dsVar('line')}` }}>
                        <Typography component="div" sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.6 }}>
                            {registryHref ? (
                                <Box
                                    component="a"
                                    href={registryHref}
                                    sx={{
                                        color: dsVar('fg-soft'), textDecoration: 'none',
                                        borderBottom: `1px dotted ${dsVar('line')}`,
                                        '&:hover': { color: dsVar('accent-fg'), borderBottomColor: dsVar('accent-fg') },
                                    }}
                                >
                                    {head}
                                </Box>
                            ) : head}
                            {ref.pinpoint && (
                                <Typography component="span" sx={{ ml: 0.75, color: dsVar('fg-2'), fontSize: '0.8125rem' }}>
                                    {ref.pinpoint}
                                </Typography>
                            )}
                        </Typography>
                        {meta && (
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>{meta}</Typography>
                        )}
                        {ref.source_link && (
                            <Box
                                component="a"
                                href={ref.source_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25,
                                    fontSize: '0.75rem', color: dsVar('accent-fg'), textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Open source <LaunchIcon sx={{ fontSize: 12 }} />
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};
